import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, ShoppingBag } from "lucide-react";
import { useApi } from "../../lib/api";
import { Reveal } from "../Reveal";
import { IssueFlipbook } from "./IssueFlipbook";
import { BuyIssue, PRICE } from "./BuyIssue";

export const IssueDownload = () => {
  const { data } = useApi("/issue");
  const [open, setOpen] = useState(false);
  const [buy, setBuy] = useState(false);
  const preview = Array.isArray(data?.preview) ? data.preview : [];
  if (!data?.on_sale || !data?.cover || data.show === false) return null;
  const meta = [data.pages ? `${data.pages} pages` : null, data.size_mb ? `${data.size_mb} MB` : null, "PDF"].filter(Boolean).join(" · ");
  const toBuy = () => { setOpen(false); setBuy(true); };
  return (
    <section className="bg-hot text-ink overflow-hidden" data-testid="issue-download">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 py-16 sm:py-24 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
        <Reveal className="md:col-span-5 flex justify-center md:justify-start [perspective:1200px]">
          <motion.button
            type="button"
            onClick={() => (preview.length ? setOpen(true) : setBuy(true))}
            initial={{ rotate: -4 }}
            whileHover={{ rotate: 0, rotateY: -12, scale: 1.03 }}
            transition={{ type: "spring", stiffness: 160, damping: 16 }}
            className="block w-[72%] sm:w-[60%] md:w-full max-w-[380px] shadow-[18px_18px_0_#1B1215] border-2 border-ink"
            data-testid="issue-cover-link"
          >
            <img src={data.cover} alt={`Cover of ${data.title}`} className="w-full aspect-[210/297] object-cover block" />
          </motion.button>
        </Reveal>
        <Reveal className="md:col-span-7" delay={0.1}>
          <p className="kicker bg-ink text-hot inline-block px-2.5 py-1.5">Print issue</p>
          <h2 className="display text-[clamp(2.75rem,6vw,6rem)] leading-[0.86] mt-5" data-testid="issue-title">{data.title}</h2>
          <p className="font-serif italic text-xl sm:text-2xl mt-5 max-w-xl">Every story of the issue, laid out like a real magazine. Flip through the first {preview.length || 10} pages for free, then get the printable PDF for {PRICE}.</p>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <button
              type="button"
              onClick={() => setBuy(true)}
              className="group inline-flex items-center gap-3 bg-ink text-cream kicker px-7 py-4 border-2 border-ink hover:bg-cream hover:text-ink transition-colors"
              data-testid="issue-buy-btn"
            >
              <ShoppingBag className="h-4 w-4 group-hover:-rotate-6 transition-transform" /> Buy the PDF · {PRICE}
            </button>
            {preview.length > 0 && (
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="group inline-flex items-center gap-3 bg-cream text-ink kicker px-7 py-4 border-2 border-ink hover:bg-ink hover:text-cream transition-colors"
                data-testid="issue-preview-btn"
              >
                <BookOpen className="h-4 w-4 group-hover:-rotate-6 transition-transform" /> Flip through {preview.length} pages
              </button>
            )}
            <span className="kicker" data-testid="issue-meta">{meta}</span>
          </div>
          <Link to="/category/culture" className="sr-only">Browse online</Link>
        </Reveal>
      </div>
      <AnimatePresence>
        {open && <IssueFlipbook pages={preview} onBuy={toBuy} total={data.pages} title={data.title} onClose={() => setOpen(false)} />}
      </AnimatePresence>
      <BuyIssue open={buy} onOpenChange={setBuy} title={data.title} />
    </section>
  );
};
